import { Badge } from '@ovComponents/2-component/badge/badge';
import { formatMoneyValue } from '@utils/moneyFormatting';
import { Box, Typography, useTheme } from 'hephaestus/web';
import { Children, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import type { RunSituationCount } from '../../types/advisorInsights.types';
import { AdvisorSituationKey } from '../../utils/insights/advisorSituationKeys';
import {
  formatRunImpactNoteLine,
  type RunImpactTranslator,
} from '../../utils/presentation/formatRunImpactSummary';

export const InsightSectionLabel = ({ children }: { children: string }) => (
  <Typography variant='headingOverline' color='textSecondary' sx={{ mb: 0.5 }}>
    {children}
  </Typography>
);

export type InsightMetricCardProps = {
  title: string;
  children: ReactNode;
  testId?: string;
  /** Nested bordered tile. Prefer `false` inside `InsightMetricsRow`. */
  framed?: boolean;
};

export const InsightMetricCard = ({
  title,
  children,
  testId,
  framed = true,
}: InsightMetricCardProps) => {
  const theme = useTheme();

  return (
    <Box
      data-testid={testId}
      sx={{
        flex: framed ? '1 1 220px' : '1 1 0',
        minWidth: 0,
        p: framed ? 1.25 : 0,
        borderRadius: framed ? theme.borderRadius.sm : 0,
        border: framed ? `1px solid ${theme.color.neutralPressed}` : 'none',
        bgcolor: framed ? theme.color.surface : 'transparent',
        display: 'flex',
        flexDirection: 'column',
        gap: 0.75,
      }}
    >
      <InsightSectionLabel>{title}</InsightSectionLabel>
      {children}
    </Box>
  );
};

export type InsightMetricsRowProps = {
  children: ReactNode;
  testId?: string;
};

/** Equal-width metric columns with a divider. Filters empty children so a tax-only row is one column. */
export const InsightMetricsRow = ({
  children,
  testId,
}: InsightMetricsRowProps) => {
  const theme = useTheme();
  const items = Children.toArray(children).filter(Boolean);
  const columnCount = Math.min(Math.max(items.length, 1), 3);

  if (items.length === 0) {
    return null;
  }

  return (
    <Box
      data-testid={testId}
      sx={{
        display: 'grid',
        gridTemplateColumns: {
          xs: '1fr',
          sm: `repeat(${columnCount}, minmax(0, 1fr))`,
        },
        columnGap: 2,
        rowGap: 1.5,
        alignItems: 'start',
        width: '100%',
        '& > *': {
          minWidth: 0,
        },
        ...(columnCount > 1
          ? {
              '& > *:not(:last-of-type)': {
                pr: { sm: 2 },
                borderRight: {
                  sm: `1px solid ${theme.color.outlineVariant}`,
                },
              },
            }
          : {}),
      }}
    >
      {items}
    </Box>
  );
};

export type InsightTickerGridsRowProps = {
  children: ReactNode;
  testId?: string;
};

export const InsightTickerGridsRow = ({
  children,
  testId,
}: InsightTickerGridsRowProps) => (
  <Box
    data-testid={testId}
    sx={{
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 200px), 1fr))',
      columnGap: 3,
      rowGap: 1.5,
      width: '100%',
    }}
  >
    {children}
  </Box>
);

type InsightMiniDonutSegment = {
  value: number;
  color: string;
};

type InsightMiniDonutProps = {
  segments: InsightMiniDonutSegment[];
  centerLabel?: string;
  size?: number;
  strokeWidth?: number;
  testId?: string;
  ariaLabel?: string;
};

const DONUT_RADIUS = 15.9155;

const InsightMiniDonut = ({
  segments,
  centerLabel,
  size = 52,
  strokeWidth = 4,
  testId = 'bulk-rebalance-insight-mini-donut',
  ariaLabel,
}: InsightMiniDonutProps) => {
  const theme = useTheme();
  const normalizedSegments = segments.filter((segment) => segment.value > 0);
  const total = normalizedSegments.reduce(
    (sum, segment) => sum + segment.value,
    0,
  );
  const circumference = 2 * Math.PI * DONUT_RADIUS;
  let dashOffset = 0;

  return (
    <Box
      data-testid={testId}
      aria-label={ariaLabel}
      role={ariaLabel ? 'img' : undefined}
      sx={{
        position: 'relative',
        width: size,
        height: size,
        flexShrink: 0,
      }}
    >
      <Box
        component='svg'
        viewBox='0 0 36 36'
        sx={{ width: size, height: size, display: 'block' }}
      >
        <circle
          cx='18'
          cy='18'
          r={DONUT_RADIUS}
          fill='none'
          stroke={theme.color.neutralHover}
          strokeWidth={strokeWidth}
        />
        {total > 0
          ? normalizedSegments.map((segment, index) => {
              const segmentLength = (segment.value / total) * circumference;
              const circle = (
                <circle
                  key={`${segment.color}-${index}`}
                  cx='18'
                  cy='18'
                  r={DONUT_RADIUS}
                  fill='none'
                  stroke={segment.color}
                  strokeWidth={strokeWidth}
                  strokeDasharray={`${segmentLength} ${circumference - segmentLength}`}
                  strokeDashoffset={-dashOffset}
                  transform='rotate(-90 18 18)'
                />
              );
              dashOffset += segmentLength;
              return circle;
            })
          : null}
      </Box>
      {centerLabel ? (
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            pointerEvents: 'none',
          }}
        >
          <Typography
            variant='labelSmall'
            sx={{ fontSize: '0.65rem', lineHeight: 1.1, fontWeight: 600 }}
          >
            {centerLabel}
          </Typography>
        </Box>
      ) : null}
    </Box>
  );
};

const formatInsightPctLabel = (value: number) =>
  `${Math.round(Math.max(0, Math.min(100, value)))}%`;

export type InsightTickerAmountRow = {
  ticker: string;
  amountCents: number;
};

type InsightTickerAmountGridProps = {
  title: string;
  rows: InsightTickerAmountRow[];
  testId?: string;
};

export const InsightTickerAmountGrid = ({
  title,
  rows,
  testId,
}: InsightTickerAmountGridProps) => {
  const theme = useTheme();

  if (rows.length === 0) {
    return null;
  }

  return (
    <Box data-testid={testId} sx={{ minWidth: 0 }}>
      <Typography
        variant='headingOverline'
        color='textSecondary'
        sx={{ mb: 0.5 }}
      >
        {title}
      </Typography>
      <Box
        component='ul'
        aria-label={title}
        sx={{
          m: 0,
          p: 0,
          listStyle: 'none',
          display: 'flex',
          flexDirection: 'column',
          gap: 0.375,
        }}
      >
        {rows.map((row) => (
          <Box
            component='li'
            key={row.ticker}
            sx={{
              display: 'flex',
              alignItems: 'baseline',
              gap: 0.75,
              minWidth: 0,
            }}
          >
            <Typography
              variant='labelSmall'
              color='textPrimary'
              sx={{ fontWeight: 600, letterSpacing: '0.04em' }}
            >
              {row.ticker}
            </Typography>
            <Box
              aria-hidden
              sx={{
                flex: 1,
                minWidth: 8,
                borderBottom: `1px dotted ${theme.color.outlineVariant}`,
                transform: 'translateY(-0.15em)',
              }}
            />
            <Typography
              variant='labelSmall'
              color='textSecondary'
              sx={{
                fontVariantNumeric: 'tabular-nums',
                whiteSpace: 'nowrap',
              }}
            >
              {formatMoneyValue(row.amountCents)}
            </Typography>
          </Box>
        ))}
      </Box>
    </Box>
  );
};

export type InsightTaxSellSplitBarProps = {
  registeredSellCents: number;
  taxableSellCents: number;
  estRealizedGainCents?: number | null;
  estRealizedGainIsComplete?: boolean;
  incompleteGainLineCount?: number;
  topTaxableSells?: InsightTickerAmountRow[];
  topShelteredSells?: InsightTickerAmountRow[];
  includeTickerGrids?: boolean;
  testId?: string;
};

export const InsightTaxSellSplitBar = ({
  registeredSellCents,
  taxableSellCents,
  estRealizedGainCents,
  estRealizedGainIsComplete = false,
  incompleteGainLineCount = 0,
  topTaxableSells = [],
  topShelteredSells = [],
  includeTickerGrids = true,
  testId = 'bulk-rebalance-insight-tax-split',
}: InsightTaxSellSplitBarProps) => {
  const { t } = useTranslation(['bulkRebalance']);
  const theme = useTheme();
  const totalSellCents = registeredSellCents + taxableSellCents;

  if (totalSellCents <= 0) {
    return (
      <Typography
        variant='bodySmall'
        color='textSecondary'
        data-testid={testId}
      >
        {t('runInsights.impact.shelteredOnly')}
      </Typography>
    );
  }

  const shelteredPct = Math.max(
    0,
    Math.min(100, (registeredSellCents / totalSellCents) * 100),
  );

  return (
    <Box
      data-testid={testId}
      sx={{ display: 'flex', gap: 1.25, alignItems: 'flex-start' }}
    >
      <InsightMiniDonut
        testId={`${testId}-donut`}
        centerLabel={formatInsightPctLabel(shelteredPct)}
        ariaLabel={t('insights.registeredSellPct', {
          value: Math.round(shelteredPct),
        })}
        segments={[
          { value: registeredSellCents, color: theme.color.positive },
          { value: taxableSellCents, color: theme.color.warning },
        ]}
      />
      <Box
        sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, minWidth: 0 }}
      >
        <Typography variant='bodySmall'>
          {t('insights.shelteredVsTaxable', {
            sheltered: formatMoneyValue(registeredSellCents),
            taxable: formatMoneyValue(taxableSellCents),
          })}
        </Typography>
        {taxableSellCents > 0 && estRealizedGainCents != null ? (
          <Typography variant='labelSmall' color={theme.color.warning}>
            {t('insights.estRealizedGainsHint', {
              value: formatMoneyValue(estRealizedGainCents),
            })}
          </Typography>
        ) : null}
        {taxableSellCents > 0 && incompleteGainLineCount > 0 ? (
          <Typography variant='labelSmall' color='textSecondary'>
            {t('insights.estRealizedGainsIncompleteLine', {
              count: incompleteGainLineCount,
            })}
          </Typography>
        ) : taxableSellCents > 0 &&
          !estRealizedGainIsComplete &&
          estRealizedGainCents == null ? (
          <Typography variant='labelSmall' color='textSecondary'>
            {t('insights.estRealizedGainsUnavailable')}
          </Typography>
        ) : taxableSellCents === 0 ? (
          <Typography variant='labelSmall' color='textSecondary'>
            {t('insights.registeredSellsHint')}
          </Typography>
        ) : null}
        {includeTickerGrids &&
        (topTaxableSells.length > 0 || topShelteredSells.length > 0) ? (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: 0.5,
              mt: 0.25,
              width: '100%',
            }}
          >
            <InsightTickerAmountGrid
              title={t('runInsights.impact.topTaxableSellsTitle')}
              rows={topTaxableSells}
              testId={`${testId}-taxable-grid`}
            />
            <InsightTickerAmountGrid
              title={t('runInsights.impact.topShelteredSellsTitle')}
              rows={topShelteredSells}
              testId={`${testId}-sheltered-grid`}
            />
          </Box>
        ) : null}
      </Box>
    </Box>
  );
};

const CASH_EVENT_BADGE_COLOR: Record<
  string,
  'supportOne' | 'supportTwo' | 'warning'
> = {
  [AdvisorSituationKey.CashRaise]: 'warning',
  [AdvisorSituationKey.CashDeploy]: 'supportOne',
  [AdvisorSituationKey.MultiCurrency]: 'supportTwo',
  LIQUIDATION: 'warning',
  FX_DETECTION: 'supportTwo',
  SWITCH_DETECTION: 'supportOne',
};

export type InsightCashEventBadgesProps = {
  notes: RunSituationCount[];
  t: RunImpactTranslator;
  testId?: string;
};

export const InsightCashEventBadges = ({
  notes,
  t,
  testId = 'bulk-rebalance-insight-cash-events',
}: InsightCashEventBadgesProps) => {
  if (notes.length === 0) {
    return null;
  }

  return (
    <Box
      data-testid={testId}
      sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', alignItems: 'center' }}
    >
      {notes.map((note) => (
        <Badge
          key={note.stage}
          label={formatRunImpactNoteLine(note, t)}
          color={CASH_EVENT_BADGE_COLOR[note.stage] ?? 'supportTwo'}
        />
      ))}
    </Box>
  );
};

export type InsightTurnoverMeterProps = {
  turnoverPct: number;
  buyPct?: number;
  sellPct?: number;
  holdingCount?: number;
  caption?: string | null;
  footnote?: string | null;
  detailLine?: string | null;
  /** Prefer `caption` for new call sites. */
  detailedLabel?: string | null;
  testId?: string;
};

const formatTurnoverWeightPct = (value: number) =>
  Number.isInteger(value) ? String(value) : value.toFixed(1);

export const InsightTurnoverMeter = ({
  turnoverPct,
  buyPct,
  sellPct,
  holdingCount,
  caption,
  footnote,
  detailLine,
  detailedLabel,
  testId = 'bulk-rebalance-insight-turnover',
}: InsightTurnoverMeterProps) => {
  const { t } = useTranslation(['bulkRebalance']);
  const theme = useTheme();

  const clampedTurnoverPct = Math.min(100, Math.max(0, turnoverPct));
  const primaryText =
    caption ??
    detailedLabel ??
    t('insights.turnoverMeterHeadline', {
      value: turnoverPct.toFixed(1),
    });
  const ariaLabel = [primaryText, footnote, detailLine]
    .filter(Boolean)
    .join(' · ');

  return (
    <Box
      data-testid={testId}
      sx={{ display: 'flex', gap: 1.25, alignItems: 'flex-start' }}
    >
      <InsightMiniDonut
        testId={`${testId}-donut`}
        centerLabel={`${clampedTurnoverPct.toFixed(1)}%`}
        ariaLabel={ariaLabel}
        segments={[
          { value: clampedTurnoverPct, color: theme.color.supportTwo },
          { value: 100 - clampedTurnoverPct, color: theme.color.neutralHover },
        ]}
      />
      <Box
        sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, minWidth: 0 }}
      >
        <Typography variant='bodySmall'>{primaryText}</Typography>
        {footnote ? (
          <Typography variant='labelSmall' color='textSecondary'>
            {footnote}
          </Typography>
        ) : null}
        {detailLine ? (
          <Typography variant='labelSmall' color='textSecondary'>
            {detailLine}
          </Typography>
        ) : null}
        {buyPct != null && sellPct != null ? (
          <Typography variant='labelSmall' color='textSecondary'>
            {t('insights.turnoverBuySell', {
              buyPct: formatTurnoverWeightPct(buyPct),
              sellPct: formatTurnoverWeightPct(sellPct),
            })}
          </Typography>
        ) : null}
        {holdingCount != null ? (
          <Typography variant='labelSmall' color='textSecondary'>
            {t('insights.turnoverHoldingCount', { count: holdingCount })}
          </Typography>
        ) : null}
      </Box>
    </Box>
  );
};

export type InsightTradeMixMeterProps = {
  buyCents: number;
  sellCents: number;
  centerLabel: string;
  caption: string;
  footnote?: string;
  topBuys?: InsightTickerAmountRow[];
  topSells?: InsightTickerAmountRow[];
  includeTickerGrids?: boolean;
  testId?: string;
};

export const InsightTradeMixMeter = ({
  buyCents,
  sellCents,
  centerLabel,
  caption,
  footnote,
  topBuys = [],
  topSells = [],
  includeTickerGrids = true,
  testId = 'bulk-rebalance-insight-trade-mix',
}: InsightTradeMixMeterProps) => {
  const { t } = useTranslation(['bulkRebalance']);
  const totalCents = buyCents + sellCents;

  if (totalCents <= 0) {
    return null;
  }

  const theme = useTheme();

  return (
    <Box
      data-testid={testId}
      sx={{ display: 'flex', gap: 1.25, alignItems: 'flex-start' }}
    >
      <InsightMiniDonut
        testId={`${testId}-donut`}
        centerLabel={centerLabel}
        ariaLabel={[caption, footnote].filter(Boolean).join(' · ')}
        segments={[
          { value: buyCents, color: theme.color.supportOneNew },
          { value: sellCents, color: theme.color.warning },
        ]}
      />
      <Box
        sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, minWidth: 0 }}
      >
        <Typography variant='bodySmall'>{caption}</Typography>
        {footnote ? (
          <Typography variant='labelSmall' color='textSecondary'>
            {footnote}
          </Typography>
        ) : null}
        {includeTickerGrids && (topSells.length > 0 || topBuys.length > 0) ? (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: 0.5,
              mt: 0.25,
              width: '100%',
            }}
          >
            <InsightTickerAmountGrid
              title={t('runInsights.impact.topBatchSellsTitle')}
              rows={topSells}
              testId={`${testId}-sells-grid`}
            />
            <InsightTickerAmountGrid
              title={t('runInsights.impact.topBatchBuysTitle')}
              rows={topBuys}
              testId={`${testId}-buys-grid`}
            />
          </Box>
        ) : null}
      </Box>
    </Box>
  );
};
